import type { CSSProperties } from 'react';

/**
 * The app's icon set: inline SVG on a 24px grid with a 1.75 stroke, drawn in
 * the Lucide style (ISC) so it sits next to SF Symbols "regular". No icon
 * dependency; each glyph is a few hundred bytes.
 *
 * `filled` is the selected-tab variant: shapes marked `f` fill with the
 * current colour, and shapes marked `k` ("knockout") are drawn in
 * `--icon-knock` on top, e.g. the check inside a filled circle.
 */

type Shape =
  | { p: string; f?: boolean; k?: boolean }
  | { c: [number, number, number]; f?: boolean; k?: boolean }
  | { r: [number, number, number, number, number]; f?: boolean; k?: boolean };

const I = {
  sun: [{ c: [12, 12, 4], f: true }, { p: 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41' }],
  tasks: [{ c: [12, 12, 10], f: true }, { p: 'm8.5 12 2.5 2.5 4.5-5', k: true }],
  mic: [{ p: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z', f: true }, { p: 'M19 10v2a7 7 0 0 1-14 0v-2M12 19v3' }],
  inbox: [
    { p: 'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z', f: true },
    { p: 'M22 12h-6l-2 3h-4l-2-3H2', k: true },
  ],
  grid: [
    { r: [3, 3, 7, 7, 1.5], f: true },
    { r: [14, 3, 7, 7, 1.5], f: true },
    { r: [14, 14, 7, 7, 1.5], f: true },
    { r: [3, 14, 7, 7, 1.5], f: true },
  ],
  more: [{ c: [5, 12, 1.2], f: true }, { c: [12, 12, 1.2], f: true }, { c: [19, 12, 1.2], f: true }],
  heart: [
    { p: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z', f: true },
    { p: 'M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27', k: true },
  ],
  wallet: [
    { p: 'M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1' },
    { p: 'M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4' },
  ],
  list: [{ p: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' }],
  users: [{ p: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }, { c: [9, 7, 4] }, { p: 'M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' }],
  folder: [{ p: 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z' }],
  calendar: [{ r: [3, 4, 18, 18, 2] }, { p: 'M16 2v4M8 2v4M3 10h18' }],
  bell: [{ p: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', f: true }, { p: 'M10.3 21a1.94 1.94 0 0 0 3.4 0' }],
  search: [{ c: [11, 11, 7.5] }, { p: 'm21 21-4.3-4.3' }],
  plus: [{ p: 'M5 12h14M12 5v14' }],
  check: [{ p: 'M20 6 9 17l-5-5' }],
  x: [{ p: 'M18 6 6 18M6 6l12 12' }],
  'chevron-right': [{ p: 'm9 18 6-6-6-6' }],
  'chevron-left': [{ p: 'm15 18-6-6 6-6' }],
  'chevron-down': [{ p: 'm6 9 6 6 6-6' }],
  clock: [{ c: [12, 12, 10] }, { p: 'M12 6v6l4 2' }],
  droplet: [{ p: 'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z' }],
  coffee: [{ p: 'M10 2v2M14 2v2M6 2v2M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1' }],
  utensils: [{ p: 'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7' }],
  undo: [{ p: 'M9 14 4 9l5-5' }, { p: 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11' }],
  settings: [
    {
      p: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
    },
    { c: [12, 12, 3] },
  ],
  filter: [{ p: 'M22 3H2l8 9.46V19l4 2v-8.54L22 3z' }],
  keyboard: [{ r: [2, 4, 20, 16, 2] }, { p: 'M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10' }],
  chat: [{ p: 'M7.9 20A9 9 0 1 0 4 16.1L2 22Z' }],
  stop: [{ r: [6, 6, 12, 12, 2.5], f: true }],
  moon: [{ p: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z' }],
  bolt: [{ p: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z' }],
  activity: [{ p: 'M22 12h-4l-3 9L9 3l-3 9H2' }],
  dumbbell: [{ p: 'M6 7v10M18 7v10M3 9.5v5M21 9.5v5M6 12h12' }],
  file: [{ p: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }, { p: 'M14 2v4a2 2 0 0 0 2 2h4M16 13H8M16 17H8M10 9H8' }],
  package: [
    { p: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' },
    { p: 'M3.29 7 12 12l8.71-5M12 22V12M16.5 9.4 7.55 4.24' },
  ],
  plane: [
    {
      p: 'M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z',
    },
  ],
  bulb: [{ p: 'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5M9 18h6M10 22h4' }],
  history: [{ p: 'M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' }, { p: 'M3 3v5h5M12 7v5l4 2' }],
  bot: [{ p: 'M12 8V4H8' }, { r: [4, 8, 16, 12, 2] }, { p: 'M2 14h2M20 14h2M15 13v2M9 13v2' }],
  plug: [{ p: 'M12 22v-5M9 8V2M15 8V2M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z' }],
  contact: [{ p: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20' }, { c: [12, 8, 2] }, { p: 'M15 13a3 3 0 1 0-6 0' }],
  shield: [
    {
      p: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    },
  ],
  receipt: [
    { p: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z' },
    { p: 'M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8M12 17.5v-11' },
  ],
  repeat: [{ p: 'm17 2 4 4-4 4M3 11v-1a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v1a4 4 0 0 1-4 4H3' }],
  trend: [{ p: 'M22 7 13.5 15.5 8.5 10.5 2 17M16 7h6v6' }],
  clipboard: [{ r: [8, 2, 8, 4, 1] }, { p: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M12 11h4M12 16h4M8 11h.01M8 16h.01' }],
  alert: [{ c: [12, 12, 10] }, { p: 'M12 8v4M12 16h.01' }],
  book: [{ p: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20' }],
  target: [{ c: [12, 12, 10] }, { c: [12, 12, 6] }, { c: [12, 12, 2] }],
  logout: [{ p: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9' }],
  'arrow-up': [{ p: 'm5 12 7-7 7 7M12 19V5' }],
  circle: [{ c: [12, 12, 10] }],
  type: [{ p: 'M4 7V4h16v3M9 20h6M12 4v16' }],
  mail: [{ r: [2, 4, 20, 16, 2] }, { p: 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' }],
  cart: [{ c: [8, 21, 1] }, { c: [19, 21, 1] }, { p: 'M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12' }],
  hourglass: [
    {
      p: 'M5 22h14M5 2h14M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2',
    },
  ],
  archive: [{ r: [2, 3, 20, 5, 1] }, { p: 'M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8M10 12h4' }],
  bed: [{ p: 'M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9' }],
  star: [
    {
      p: 'M11.53 2.3a.53.53 0 0 1 .95 0l2.31 4.68a2.12 2.12 0 0 0 1.6 1.16l5.16.76a.53.53 0 0 1 .3.9l-3.74 3.64a2.12 2.12 0 0 0-.61 1.88l.88 5.14a.53.53 0 0 1-.77.56l-4.62-2.43a2.12 2.12 0 0 0-1.97 0L6.4 21.01a.53.53 0 0 1-.77-.56l.88-5.14a2.12 2.12 0 0 0-.61-1.88L2.16 9.8a.53.53 0 0 1 .3-.91l5.16-.75a2.12 2.12 0 0 0 1.6-1.16z',
      f: true,
    },
  ],
  sort: [{ p: 'm3 16 4 4 4-4M7 20V4M21 8l-4-4-4 4M17 4v16' }],
  pencil: [{ p: 'M21.17 6.81a1 1 0 0 0-3.98-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5zM15 5l4 4' }],
  trash: [{ p: 'M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2' }],
} satisfies Record<string, Shape[]>;

export type IconName = keyof typeof I;
export const ICON_NAMES = Object.keys(I) as IconName[];

export default function Icon({
  name,
  size = 20,
  filled = false,
  className,
  title,
  style,
}: {
  name: IconName;
  size?: number;
  filled?: boolean;
  className?: string;
  /** An accessible name; without one the icon is decorative (aria-hidden). */
  title?: string;
  style?: CSSProperties;
}): JSX.Element {
  const shapes: readonly Shape[] = I[name];
  return (
    <svg
      className={`icon${filled ? ' icon--filled' : ''}${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={style}
    >
      {shapes.map((s, i) => {
        const fill = filled && s.f ? 'currentColor' : 'none';
        // A knockout stroke only shows against a filled shape.
        const stroke = filled && s.k ? 'var(--icon-knock, #fff)' : undefined;
        if ('p' in s) return <path key={i} d={s.p} fill={fill} stroke={stroke} />;
        if ('c' in s) return <circle key={i} cx={s.c[0]} cy={s.c[1]} r={s.c[2]} fill={fill} stroke={stroke} />;
        return <rect key={i} x={s.r[0]} y={s.r[1]} width={s.r[2]} height={s.r[3]} rx={s.r[4]} fill={fill} stroke={stroke} />;
      })}
    </svg>
  );
}
